import { useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { measurementToChartData } from '@/features/harness/utils/measurementToChartData';
import { NyquistPlot } from '@/components/MeasurementAnalysis/Charts/NyquistPlot';
import { BodePlot } from '@/components/MeasurementAnalysis/Charts/BodePlot';
import { generateTemporalPalette } from '@/components/MeasurementAnalysis/Charts/useChartData';
import type { MeasurementDto } from '@/features/harness/types';

export interface MeasurementPlotViewProps {
  /** All available Measurements (live store list, or a Batch's fetched Measurements). */
  measurements: MeasurementDto[];
  /** Selected Measurement ids in run order — the curves to overlay. */
  selectedIds: string[];
}

/**
 * Presentational core of the Measurement plot: overlays the selected Measurements' curves (AC1)
 * by reusing the shared NyquistPlot/BodePlot components unmodified via the `measurementToChartData`
 * adapter, colouring each by selection order (a temporal blue→red palette) exactly as the analysis
 * space's overlay does. Reads nothing from the store, so the live harness view and the
 * connection-free review view (Story 3.1) both feed it their own measurements/selection.
 */
export function MeasurementPlotView({ measurements, selectedIds }: MeasurementPlotViewProps) {
  const [view, setView] = useState<'nyquist' | 'bode'>('nyquist');
  // Channels the user has explicitly hidden. Empty = every available channel is shown; a newly
  // appearing channel (from a later selection) defaults to visible since it isn't in this set.
  const [hiddenChannels, setHiddenChannels] = useState<Set<string>>(() => new Set());

  // Resolve the selected Measurements in the given SequenceNumber order (which is already the
  // order of `selectedIds`), so the overlay's temporal palette maps oldest→newest.
  const selected = useMemo(
    () =>
      selectedIds
        .map((id) => measurements.find((m) => m.id === id))
        .filter((m): m is NonNullable<typeof m> => m != null),
    [selectedIds, measurements],
  );

  const testData = useMemo(() => selected.map((m) => measurementToChartData(m)), [selected]);

  // Union of every selected curve's channels — different Measurements may expose different
  // channel sets. This is the full set the user can show/hide.
  const allChannels = useMemo(() => {
    const channels = new Set<string>();
    for (const t of testData) {
      for (const c of t.baseline?.channels ?? []) channels.add(c.channelId);
    }
    return channels;
  }, [testData]);

  // Sorted list of channels for the toggle UI (stable order regardless of selection order).
  const channelList = useMemo(() => [...allChannels].sort(), [allChannels]);

  // A curve is drawn only for channels in this set: every available channel the user hasn't hidden.
  const visibleChannels = useMemo(
    () => new Set(channelList.filter((c) => !hiddenChannels.has(c))),
    [channelList, hiddenChannels],
  );

  const toggleChannel = (channel: string, checked: boolean) =>
    setHiddenChannels((prev) => {
      const next = new Set(prev);
      if (checked) next.delete(channel);
      else next.add(channel);
      return next;
    });

  // Temporal palette (blue→red by selection order), keyed by testId — the same scheme the
  // analysis space uses so a harness overlay reads consistently.
  const colorMap = useMemo(() => {
    const colors = generateTemporalPalette(testData.length);
    return new Map(testData.map((t, i) => [t.testId, colors[i]]));
  }, [testData]);

  if (selected.length === 0) {
    return null;
  }

  const curveCount = testData.filter((t) => t.baseline != null).length;
  const hasCurve = curveCount > 0;

  const heading =
    selected.length === 1
      ? `Measurement #${selected[0].sequenceNumber}`
      : `Comparing ${selected.length} Measurements`;

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2">{heading}</Typography>
        {/* Only offer the view toggle when there is actually a curve to switch views on. */}
        {hasCurve && (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={view}
            onChange={(_, v) => v && setView(v)}
          >
            <ToggleButton value="nyquist">Nyquist</ToggleButton>
            <ToggleButton value="bode">Bode</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>
      {/* Per-channel show/exclude — only worth showing when a curve exposes more than one channel. */}
      {hasCurve && channelList.length > 1 && (
        <FormGroup row sx={{ mb: 1 }}>
          {channelList.map((ch) => (
            <FormControlLabel
              key={ch}
              control={
                <Checkbox
                  size="small"
                  checked={visibleChannels.has(ch)}
                  onChange={(e) => toggleChannel(ch, e.target.checked)}
                  inputProps={{ 'aria-label': `Show channel ${ch}` }}
                />
              }
              label={ch}
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
            />
          ))}
        </FormGroup>
      )}
      <Box sx={{ height: 400 }}>
        {!hasCurve ? (
          <Typography variant="body2" color="text.secondary">
            No curve data available for {selected.length === 1 ? 'this Measurement' : 'the selected Measurements'}.
          </Typography>
        ) : view === 'nyquist' ? (
          <NyquistPlot
            testData={testData}
            viewMode="baselines"
            visibleChannels={visibleChannels}
            colorMap={colorMap}
          />
        ) : (
          <BodePlot
            testData={testData}
            viewMode="baselines"
            visibleChannels={visibleChannels}
            phaseUnit="rad"
            yScale="linear"
            colorMap={colorMap}
          />
        )}
      </Box>
    </Box>
  );
}
